import React from 'react';
import { Button, Spinner } from 'reactstrap';
import { UserSession } from '../../services/chatService';
import { Plus, MessageCircle, Trash2 } from 'react-feather';
import { normalizeTimestamp } from '../../utils/date';
import '../../assets/scss/components/chat/_session-sidebar.scss';

interface SessionSidebarProps {
	sessions: UserSession[];
	currentSessionId: string | null;
	isLoading: boolean;
	onSessionSelect: (sessionId: string) => void;
	onNewChat: () => void;
	isOpen: boolean;
	onToggle: () => void;
	onSessionDelete: (sessionId: string, e: React.MouseEvent) => void;
}

const SessionSidebar: React.FC<SessionSidebarProps> = ({
	sessions,
	currentSessionId,
	isLoading,
	onSessionSelect,
	onNewChat,
	isOpen,
	onToggle,
	onSessionDelete
}) => {
	const formatDate = (dateString: string) => {
		const date = normalizeTimestamp(dateString);
		const now = new Date();
		const diffTime = Math.abs(now.getTime() - date.getTime());
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

		if (diffDays === 1) return 'Today';
		if (diffDays === 2) return 'Yesterday';
		if (diffDays <= 7) return `${diffDays - 1} days ago`;
		return date.toLocaleDateString();
	};

	const truncateText = (text: string, maxLength: number = 50) => {
		if (text.length <= maxLength) return text;
		return text.substring(0, maxLength) + '...';
	};

	return (
		<>
			{/* Overlay for when sidebar is open on iPad/tablet */}
			{isOpen && (
				<div className="session-sidebar-overlay" onClick={onToggle} />
			)}

			<div className={`session-sidebar ${isOpen ? 'open' : ''}`}>
				<div className="sidebar-header">
					<h6 className="sidebar-title">Chat History</h6>
					<Button
						color="primary"
						size="sm"
						onClick={onNewChat}
						className="new-chat-btn"
					>
						<Plus size={16} />
						New Chat
					</Button>
				</div>

				<div className="sidebar-content">
					{isLoading ? (
						<div className="loading-container">
							<Spinner size="sm" />
							<span>Loading sessions...</span>
						</div>
					) : sessions.length === 0 ? (
						<div className="empty-state">
							<MessageCircle size={32} className="empty-icon" />
							<p>No chat history yet</p>
							<small>Start a new conversation to see it here</small>
						</div>
					) : (
						<div className="sessions-list">
							{sessions.map((session) => (
								<div
									key={session.id}
									className={`session-item ${currentSessionId === session.id ? 'active' : ''
										}`}
									onClick={() => onSessionSelect(session.id)}
								>
									<div className="session-title">
										{truncateText(session.session_title)}
									</div>
									<div className="session-meta">
										<span className="session-date">
											{formatDate(session.updated_at)}
										</span>
										<span className="session-count">
											{session.message_count} messages
										</span>
									</div>
									<div className="session-preview">
										{truncateText(session.last_message, 60)}
									</div>
									<div className="session-actions">
										<Button
											color="link"
											size="sm"
											className="delete-session-btn"
											onClick={(e) => onSessionDelete(session.id, e)}
											title="Delete session"
										>
											<Trash2 size={14} />
										</Button>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export default SessionSidebar;